/**
 * MKS-142 — full-text search helpers for the project board (KanbanBoardPage).
 *
 * Pure client-side matching over ticket code / name / description —
 * case-insensitive AND diacritic-insensitive in both directions ('ucet'
 * finds 'Účetnictví'; 'účet' finds 'Ucetnictvi'). Plain substring match, no
 * tokenizing and no RegExp construction from user input (threat T-142-02).
 *
 * The NFD-strip normalization mirrors backend/src/services/projectSlug.ts.
 */
import type { BoardColumn, TaskCard } from '../services/api/board';

/**
 * Lowercased, trimmed, diacritic-free form of the input.
 * NFD decomposition splits accented letters into base char + combining mark;
 * the replace strips the combining-diacritical-marks block (U+0300–U+036F).
 */
export function normalizeSearchText(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritical marks
    .toLowerCase()
    .trim();
}

/**
 * True when the normalized query is a substring of the task's normalized
 * code, name or description. An empty (or whitespace-only) query matches
 * every task. Null-safe: `code` and `description` may be null/undefined.
 */
export function taskMatchesSearch(
  task: Pick<TaskCard, 'code' | 'name' | 'description'>,
  query: string,
): boolean {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery === '') return true;
  return [task.code, task.name, task.description]
    .filter((field): field is string => field != null)
    .some((field) => normalizeSearchText(field).includes(normalizedQuery));
}

/**
 * Narrows each column's tasks to those matching the query. Never drops a
 * column (D-04 — empty columns stay visible and remain drag targets).
 * Returns the SAME array reference when the query is blank so consuming
 * useMemos keep downstream props referentially stable.
 */
export function filterBoardColumnsBySearch(columns: BoardColumn[], query: string): BoardColumn[] {
  if (normalizeSearchText(query) === '') return columns;
  return columns.map((col) => ({
    ...col,
    tasks: col.tasks.filter((t) => taskMatchesSearch(t, query)),
  }));
}
